/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  DeleteFilled,
  EyeOutlined,
  FileUnknownOutlined,
  LoadingOutlined,
  ProfileOutlined,
} from '@ant-design/icons'
import { Button, Popconfirm, Space, Tag, Tooltip, Table } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import Text from 'antd/lib/typography/Text'
import { format } from 'date-fns'
import moment from 'moment'
import React from 'react'
import { Link } from 'react-router-dom'
import { useTicket } from '~/hooks/HelpDesk/useTickets'
import { useTaskType } from '~/hooks/Tasks/TaskType/useTaskType'

import { translate } from '~/utils/locale'

interface EquipmentsTasks {
  id: string
}

interface TaskListProps {
  checkin_type: string
  createdAt: Date
  date: string
  duration_time: string
  external_code: string
  is_enabled: string
  priority: string
  _id: string
  hours: string
  currentDate: string
  internal_code: string
  current_status: string
  customer_id: string
  task_type_id: string
  responsible: {
    id: string
    type: string
  }
  address: {
    city: string
    id: string
    location: {
      x: number
      y: number
    }
    neighborhood: string
    number: number
    state: string
    street: string
    zip_code: string
  }
  equipments: EquipmentsTasks[]
}

const TableTasksTickets: React.FC = () => {
  const { ticket, isFetching, deleteTaskTicket } = useTicket()
  const { taskType } = useTaskType()

  const taskFormatted = ticket?.tasks?.map((task: any) => {
    const dataFormat = {
      ...task,
      initialTime: format(
        new Date(task.date),
        `dd/MM/yyyy '${translate('TasksTable.dateString')}' HH:mm'h'`,
      ),
    }

    return dataFormat
  })

  const columns: ColumnsType<TaskListProps> = [
    {
      title: translate('TasksTable.internalCodeColumn'),
      dataIndex: 'internal_code',
      key: 'internal_code',
      render: (name: string, record) => (
        <Tooltip title="Ver tarefa">
          <Link to={`/tarefas/visualizar/${record._id}`}>
            <Space>
              <EyeOutlined />
              {name}
            </Space>
          </Link>
        </Tooltip>
      ),
    },
    {
      title: translate('TasksTable.responsibleColumn'),
      key: 'responsible',
      defaultSortOrder: 'ascend',
      sortDirections: ['ascend', 'descend', 'ascend'],
      render: (record) => (
        <>
          {record.responsible?.type === 'collaborator' ? (
            <>
              {record?.responsible?.data?.contact?.name ? (
                <Tooltip title="Ver colaborador">
                  <Link
                    to={`/colaboradores/visualizar/colaborador/${record?.responsible?.id}/contato/${record?.responsible?.data?.contact?.id}`}
                  >
                    <Space>
                      <EyeOutlined />
                      {record?.responsible?.data?.contact?.name}
                    </Space>
                  </Link>
                </Tooltip>
              ) : (
                <Text type="secondary">
                  <Space size="small">
                    <FileUnknownOutlined />
                    N/A
                  </Space>
                </Text>
              )}
            </>
          ) : (
            <>
              {record?.responsible?.data?.title ? (
                <Text>{record?.responsible?.data?.title}</Text>
              ) : (
                <Text type="secondary">
                  <Space size="small">
                    <FileUnknownOutlined />
                    N/A
                  </Space>
                </Text>
              )}
            </>
          )}
        </>
      ),
    },
    {
      title: translate('TasksTable.initialTimeColumn'),
      dataIndex: 'initialTime',
      key: 'date',
      sorter: (a: TaskListProps, b: TaskListProps) =>
        moment(a.createdAt).unix() - moment(b.createdAt).unix(),
    },
    {
      title: translate('TasksTable.taskTypeColumn'),
      key: 'taskType',
      render: (record) => {
        const matchedTask = taskType?.find(
          (task) => task._id === record.task_type_id,
        )

        return matchedTask ? (
          <Text>{matchedTask.title}</Text>
        ) : (
          <Text type="secondary">
            <Space size="small">
              <FileUnknownOutlined />
              N/A
            </Space>
          </Text>
        )
      },
    },
    {
      title: translate('TasksTable.statusColumn'),
      key: 'status',
      align: 'center',
      render: (record) => (
        <>
          {record.current_status === 'received' ? (
            <Tag color="#096dd9">
              {translate('TasksOptionsTable.statusReceived')}
            </Tag>
          ) : (
            ''
          )}
          {record.current_status === 'viewed' ? (
            <Tag color="#2db7f5">
              {translate('TasksOptionsTable.statusViewed')}
            </Tag>
          ) : (
            ''
          )}
          {record.current_status === 'en-route' ? (
            <Tag color="#521e9b">
              {translate('TasksOptionsTable.statusEnRoute')}
            </Tag>
          ) : (
            ''
          )}
          {record.current_status === 'working' ? (
            <Tag color="#088b55">
              {translate('TasksOptionsTable.statusWorking')}
            </Tag>
          ) : (
            ''
          )}
          {record.current_status === 'break' ? (
            <Tag color="#fa8c16">
              {translate('TasksOptionsTable.statusBreak')}
            </Tag>
          ) : (
            ''
          )}
          {record.current_status === 'resume-working' ? (
            <Tag color="#088b55">
              {translate('TasksOptionsTable.statusResumeWorking')}
            </Tag>
          ) : (
            ''
          )}
          {record.current_status === 'canceled' ? (
            <Tag color="#ce151e">
              {translate('TasksOptionsTable.statusCanceled')}
            </Tag>
          ) : (
            ''
          )}
          {record.current_status === 'finished' ? (
            <Tag color="#0f4b83">
              {translate('TasksOptionsTable.statusFinished')}
            </Tag>
          ) : (
            ''
          )}
        </>
      ),
    },
    {
      title: translate('TasksTable.equipmentColumn'),
      key: 'equipments',
      align: 'center',
      render: (record) => (
        <>
          {record.equipments.length === 1 ? (
            <Tag>1 {translate('TasksTable.tagEquipment')}</Tag>
          ) : (
            <Tag>
              {record.equipments.length} {translate('TasksTable.tagEquipments')}
            </Tag>
          )}
        </>
      ),
    },
    {
      title: translate('TasksTable.actionsColumn'),
      key: 'remove',
      align: 'center',
      render: (record) => (
        <Space size="small">
          <Tooltip title={translate('TasksTable.viewTooltip')}>
            <Link to={`/tarefas/visualizar/${record._id}`} target="_blank">
              <Button
                type="primary"
                ghost
                shape="circle"
                size="small"
                icon={<ProfileOutlined />}
              />
            </Link>
          </Tooltip>
          <Popconfirm
            title={translate('TasksTable.unlinkPopconfirm')}
            onConfirm={() => deleteTaskTicket(record._id)}
          >
            <Tooltip title={translate('TasksTable.unlinkTask')}>
              <Button
                ghost
                danger
                shape="circle"
                icon={<DeleteFilled />}
                size="small"
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <Table
      rowKey={(record) => record._id}
      loading={{
        indicator: <LoadingOutlined />,
        spinning: isFetching,
        size: 'large',
      }}
      columns={columns}
      size="small"
      dataSource={taskFormatted}
      scroll={{ x: 1000 }}
    />
  )
}

export default TableTasksTickets
